import { Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { useAtom } from 'jotai'
import React from 'react'
import { useNavigate } from 'react-router'

import { viewTypeAtom } from '../../contexts/AtomUtil'
import { navigationMap } from '../../data/navMap'

/**
 * NavigationItems Component
 *
 * Renders the navigation menu items with role-based access control
 *
 * @param {Object} props
 * @param {Object} props.user - The current user object
 * @param {Function} props.onNavigate - Callback function when navigation occurs
 * @param {Object} props.sx - Additional styles for the component
 * @param {Object} props.titleSx - Additional styles for section titles
 * @param {Object} props.itemSx - Additional styles for menu items
 * @param {Object} props.textProps - Props for the ListItemText component
 */
const NavigationItems = ({ user, onNavigate, sx = {}, titleSx = {}, itemSx = {}, textProps = {} }) => {
  const navigate = useNavigate()
  const [viewType, setViewType] = useAtom(viewTypeAtom)

  const handleNavigation = (path, item) => {
    // 如果导航项目指定了view，且与当前视图不同，则切换视图
    if (item.view && item.view !== viewType) {
      setViewType(item.view)
    }
    navigate(path)
    onNavigate?.()
  }

  return (
    <div style={sx}>
      {navigationMap.map((section, index) => (
        <React.Fragment key={section.title}>
          {/* Only render section if it has visible items */}
          {section.items.some((item) => !item.roles || item.roles.some((role) => user?.roles?.includes(role))) && (
            <>
              {/* Section Title */}
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  mt: index === 0 ? 0 : 2,
                  mb: 0.5,
                  ...titleSx,
                }}
              >
                {section.title}
              </Typography>
              {/* Section Menu Items */}
              <List>
                {section.items.map(
                  (item) =>
                    // Only render item if user has required role
                    (!item.roles || item.roles.some((role) => user?.roles?.includes(role))) && (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigation(item.path, item)}
                          sx={{
                            borderRadius: 1,
                            mb: 0,
                            gap: 1,
                            '& .MuiListItemIcon-root': {
                              minWidth: 'unset',
                            },
                            ...itemSx,
                          }}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.label} {...textProps} />
                        </ListItemButton>
                      </ListItem>
                    ),
                )}
              </List>
              {/* Add divider between sections except for the last one */}
              {index < navigationMap.length - 1 && <Divider sx={{ my: 2 }} />}
            </>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

export default NavigationItems
